import { useState } from "react";
import {
  useStartRecurlySubscriptionMutation,
  useReactivateRecurlySubscriptionMutation,
  ReactivateRecurlySubscriptionMutation,
  GetRecurlyBillingDataDocument,
  UsesRecurlySubscriptionDocument,
} from "@ugitgud/legos/queries/lol";
import { grecaptchaExecute } from "lib/form-helpers";

// Re-export hooks from legos
export {
  usePremiumPlanPrices,
  useBillingData,
  useAddPaymentMethod,
  useCancelSubscription,
  useSetDefaultPaymentMethod,
  useDeletePaymentMethod,
  useRecurlySubscriptionStatus,
} from "@ugitgud/legos/hooks/usePremiumHooks";

interface StartSubscriptionResponse {
  success: boolean;
  error?: string | null;
  subscription?: {
    status?: string | null;
    cancelAtPeriodEnd?: boolean | null;
    currentPeriodEnd?: number | null;
  } | null;
}

interface HookCallbacks<T> {
  onCompleted?: (data: T) => void;
  onError?: (error: string) => void;
}

export const useStartSubscription = ({ onCompleted, onError }: HookCallbacks<StartSubscriptionResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [startSubscriptionMutation] = useStartRecurlySubscriptionMutation({
    context: { clientName: "lol" },
    refetchQueries: [
      { query: GetRecurlyBillingDataDocument, context: { clientName: "lol" } },
      { query: UsesRecurlySubscriptionDocument, context: { clientName: "lol" } },
    ],
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.premiumSignupRecurly?.success) {
        onCompleted?.(data.premiumSignupRecurly);
      } else {
        const errorMsg = data.premiumSignupRecurly?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while processing your subscription";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const startSubscription = (recurlyToken: string, planId: string) => {
    setIsLoading(true);
    setError(null);

    grecaptchaExecute(
      "checkoutSignup",
      (recaptchaToken: string) => {
        startSubscriptionMutation({
          variables: {
            planId,
            recurlyToken,
            recaptchaToken,
            recaptchaVersion: "v3",
          },
        });
      },
      () => {
        setIsLoading(false);
        const errorMsg = "reCAPTCHA verification failed. Please try again.";
        setError(errorMsg);
        onError?.(errorMsg);
      },
    );
  };

  return { startSubscription, isLoading, error };
};

type ReactivateSubscriptionResponse = NonNullable<ReactivateRecurlySubscriptionMutation["reactivateRecurlySubscription"]>;

export const useReactivateSubscription = ({ onCompleted, onError }: HookCallbacks<ReactivateSubscriptionResponse> = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [reactivateSubscriptionMutation] = useReactivateRecurlySubscriptionMutation({
    context: { clientName: "lol" },
    refetchQueries: [
      { query: GetRecurlyBillingDataDocument, context: { clientName: "lol" } },
      { query: UsesRecurlySubscriptionDocument, context: { clientName: "lol" } },
    ],
    onCompleted: (data) => {
      setIsLoading(false);
      if (data.reactivateRecurlySubscription?.success) {
        onCompleted?.(data.reactivateRecurlySubscription);
      } else {
        const errorMsg = data.reactivateRecurlySubscription?.error || "An error occurred";
        setError(errorMsg);
        onError?.(errorMsg);
      }
    },
    onError: (err) => {
      setIsLoading(false);
      const errorMsg = err.message || "An error occurred while reactivating your subscription";
      setError(errorMsg);
      onError?.(errorMsg);
    },
  });

  const reactivateSubscription = (recurlyToken: string) => {
    setIsLoading(true);
    setError(null);

    grecaptchaExecute(
      "reactivateSubscription",
      (recaptchaToken: string) => {
        reactivateSubscriptionMutation({
          variables: {
            recurlyToken,
            recaptchaToken,
            recaptchaVersion: "v3",
          },
        });
      },
      () => {
        setIsLoading(false);
        const errorMsg = "reCAPTCHA verification failed. Please try again.";
        setError(errorMsg);
        onError?.(errorMsg);
      },
    );
  };

  return { reactivateSubscription, isLoading, error };
};
